import { preferenceStorage } from '@/common/state/preferenceStorage'
import { useApi } from '@/common/state/stores/apiStore'
import { isEntitledByFreeGrant } from '@/common/utils/subscriptionEntitlements'
import { Features, OnboardingState, Subscription, User } from '@/shared/api'
import { grantsFullAccess } from '@/shared/utils/entitlements'
import { ZonedDateTime } from '@js-joda/core'
import { defineStore } from 'pinia'

export const ONBOARDING_SCHEMA_VERSION = 3

interface AccountTokenData {
  token: string
  expires_at: ZonedDateTime
  features: Features
}

interface State {
  authToken: string | null
  currentUser: User | null
  subscription: Subscription | null
  primaryToken: AccountTokenData | null
  primaryCalendarAccountId: string | null
  primaryCalendarProvider: string | null
  /** @deprecated Use primaryToken instead */
  googleToken: AccountTokenData | null
  calendarAccountTokens: Map<string, AccountTokenData>
  initialized: boolean
  subscribed: boolean
  logoutHandler: () => void
}

export const useAuthStore = defineStore('authStore', {
  state: (): State => ({
    authToken: null,
    currentUser: null,
    subscription: null,
    primaryToken: null,
    primaryCalendarAccountId: null,
    primaryCalendarProvider: null,
    googleToken: null,
    calendarAccountTokens: new Map<string, AccountTokenData>(),
    initialized: false,
    subscribed: false,
    logoutHandler: () => {}
  }),
  getters: {
    isSubscribed: (state) => {
      return state.subscribed
    },
    subscriptionSku: (state): string | null | undefined => {
      return state.subscription?.entitlements?.sku
    },
    hasFullAccess: (state): boolean => {
      // Shared predicate — the main process gates the global quick-search
      // shortcut on the exact same rule (see @/shared/utils/entitlements).
      return grantsFullAccess(state.subscription)
    },
    // MOBILE_ACCESS held via a permanent free grant rather than a purchase.
    // Entitlements are identical to a paid MOBILE_ACCESS plan — this only
    // affects how billing surfaces present the account.
    //
    // Derived, not sent — the payload names the grant once, as
    // `has_free_mobile_grant`. See `isEntitledByFreeGrant` for why the
    // derivation holds.
    isFreeMobileAccess: (state): boolean => {
      return isEntitledByFreeGrant(state.subscription)
    },
    // Whether the user holds the permanent grant at all, including while a
    // purchased plan supersedes it. Plan pickers must gate the Mobile offer on
    // this rather than on `isFreeMobileAccess`: that flag goes false the moment
    // a purchase entitles them, which would put the Mobile plan back in front
    // of a grant holder and charge them for what they already own for free.
    hasFreeMobileGrant: (state): boolean => {
      return state.subscription?.has_free_mobile_grant === true
    },
    // Whether the user is actually paying us. Use this, not `isSubscribed`,
    // for anything about managing or purchasing a plan: a granted user is
    // active but has no subscription to manage and can still buy Full Access.
    hasPaidSubscription: (state): boolean => {
      return state.subscription?.is_active === true && !isEntitledByFreeGrant(state.subscription)
    },
    isAuthenticated: (state) => {
      return !!state.authToken
    },
    isInitialized: (state) => {
      return state.initialized
    },
    integrationFeatures: (state): Features | undefined => {
      // Prefer the live entry in calendarAccountTokens — it's updated on
      // every token refresh and reflects the latest scope-authorization
      // state from the backend. primaryToken/googleToken are session-init
      // snapshots and go stale when scopes are revoked mid-session.
      const live = state.primaryCalendarAccountId
        ? state.calendarAccountTokens.get(state.primaryCalendarAccountId)
        : undefined
      return (live ?? state.primaryToken ?? state.googleToken)?.features
    },
    missingRequiredFeatures: (state): string[] | undefined => {
      const live = state.primaryCalendarAccountId
        ? state.calendarAccountTokens.get(state.primaryCalendarAccountId)
        : undefined
      const token = live ?? state.primaryToken ?? state.googleToken
      return Object.keys(token?.features ?? {}).filter(
        (feature) => !token?.features[feature].is_authorized && token?.features[feature].required
      )
    }
  },
  actions: {
    async initialize(logoutHandler: () => void) {
      const debugToken = import.meta.env.VITE_DEBUG_TOKEN as string | undefined
      if (debugToken) {
        console.warn('[AUTH] Using debug token — impersonating user')
        await this.login(debugToken)
        this.initialized = true
        this.logoutHandler = logoutHandler
        return
      }

      const [authToken, currentUser, subscription] = await Promise.all([
        loadAuthToken(),
        loadCurrentUser(),
        loadSubscription()
      ])
      if (authToken) {
        this.setCurrentUser(currentUser)
        this.subscribed = subscription?.is_active ?? false
        this.subscription = subscription
        await this.login(authToken)
      } else {
        console.warn('[AUTH] No auth token found')
      }

      this.initialized = true
      this.logoutHandler = logoutHandler
    },
    setCurrentUser(user: User | null) {
      this.currentUser = user
    },
    updateSubscription(subscription: Subscription) {
      this.subscription = subscription
      this.subscribed = subscription.is_active
      // Don't save mock subscriptions to storage
      const testSku = import.meta.env.VITE_MOBILE_TEST_SKU
      if (!(testSku && subscription.provider === 'test')) {
        saveSubscription(subscription)
      }
    },
    updateOnboardingState(state: OnboardingState) {
      if (this.currentUser) {
        this.currentUser.onboarding_state = { ...this.currentUser.onboarding_state, ...state }
        saveUser(this.currentUser)
        useApi()
          .me()
          .meOnboardingUpdate(
            {
              onboardingState: state
            },
            {
              params: {
                version: ONBOARDING_SCHEMA_VERSION
              }
            }
          )
      }
    },
    async loadFromCache() {
      const [currentUser, subscription] = await Promise.all([loadCurrentUser(), loadSubscription()])
      this.setCurrentUser(currentUser)
      this.subscription = subscription
      this.subscribed = subscription?.is_active ?? false
    },
    async login(token: string) {
      useApi().setAccessToken(token)
      try {
        const getUserPromise = useApi()
          .me()
          .meRetrieve({
            params: {
              onboarding_state_version: ONBOARDING_SCHEMA_VERSION
            }
          })

        if (!this.currentUser) {
          this.setCurrentUser((await getUserPromise).data)
          saveUser(this.currentUser)
        } else {
          // We already have a cached user, so this refresh runs in the
          // background. It must have its own .catch: an expired stored token
          // rejects here with a 401, and without a handler that became an
          // unhandled rejection flooding Sentry at startup (MOBILE-33).
          getUserPromise
            .then((user) => {
              this.setCurrentUser(user.data)
              saveUser(this.currentUser)
            })
            .catch((error) => {
              // A 401 means the stored token is genuinely invalid — clear the
              // session so the app routes to sign-in. Use logoutHandler (the
              // full teardown: resets stores, clears the local DB + FCM
              // registration, and routes to sign-in), NOT the bare logout()
              // action which only drops the token and would leave stale
              // per-user data behind. logoutHandler is already set by the time
              // this background refresh resolves. Transient/offline errors (no
              // response) are left alone so we don't log users out on a blip.
              if ((error as any)?.response?.status === 401) {
                // logoutHandler is async (clears Dexie/storage) and this call is
                // fire-and-forget — catch so a failed teardown doesn't become an
                // unhandled rejection.
                Promise.resolve(this.logoutHandler()).catch((e) =>
                  console.warn('[auth] logout after 401 did not complete cleanly:', e)
                )
              } else {
                console.warn(
                  '[auth] background user refresh failed:',
                  error instanceof Error ? error.message : error
                )
              }
            })
        }

        this.authToken = token
        saveAuthToken(token)
        this.fetchPrimaryToken()
      } catch (e) {
        // An error here means we might be offline or unauthenticated.
        // useApi will manage the offline state for us.

        this.authToken = token
      }
    },
    async refreshCurrentUser() {
      const response = await useApi()
        .me()
        .meRetrieve({
          params: {
            onboarding_state_version: ONBOARDING_SCHEMA_VERSION
          }
        })
      this.setCurrentUser(response.data)
      saveUser(this.currentUser)
      return this.currentUser
    },
    async logout() {
      useApi().setAccessToken(null)
      this.authToken = null
      this.calendarAccountTokens.clear()
      saveAuthToken(null)
      saveUser(null)
      saveSubscription(null)
    },

    getCurrentUser() {
      return this.currentUser
    },

    async fetchPrimaryToken() {
      try {
        const accounts = await useApi().me().listCalendarAccounts()
        const primary = accounts.data?.results?.find((a: any) => a.is_primary)
        if (!primary) return

        this.primaryCalendarAccountId = primary.id
        this.primaryCalendarProvider = primary.provider
        const tokenData = await this.getCalendarAccountToken(primary.id, true)
        this.primaryToken = tokenData
        // Keep googleToken in sync for backward compatibility
        if (primary.provider === 'google') {
          this.googleToken = tokenData
        }
      } catch (e) {
        // Non-fatal — user may not have a calendar account yet (fresh signup)
        console.warn('[AUTH] Could not fetch primary calendar account token:', e)
      }
    },

    /** @deprecated Use fetchPrimaryToken / getCalendarAccountToken instead */
    async getGoogleToken(forceRefetch: boolean = false) {
      // Route through the primary token path — works for both Google and Microsoft
      if (!this.primaryCalendarAccountId) {
        await this.fetchPrimaryToken()
      }
      // Return the primary token (googleToken is kept in sync by fetchPrimaryToken for Google accounts)
      return this.primaryToken ?? this.googleToken ?? null
    },

    async getCalendarAccountToken(calendarAccountId: string, forceRefetch: boolean = false) {
      const cachedToken = this.calendarAccountTokens.get(calendarAccountId)
      const isNotExpiringSoon =
        cachedToken && cachedToken.expires_at.isAfter(ZonedDateTime.now().plusMinutes(5))
      if (!forceRefetch && isNotExpiringSoon) {
        return cachedToken
      }

      // If we have a cached token that's expiring soon, force refresh from backend
      const shouldForceRefresh = forceRefetch || !isNotExpiringSoon

      const accountToken = await loadCalendarAccountToken(
        calendarAccountId,
        this.logoutHandler,
        shouldForceRefresh || false
      )
      this.calendarAccountTokens.set(calendarAccountId, accountToken)
      return accountToken
    },

    clearClandarAccountToken(calendarAccountId: string) {
      this.calendarAccountTokens.delete(calendarAccountId)
    }
  }
})

const loadAuthToken = async (): Promise<string | null> => {
  return await preferenceStorage.get('authToken')
}

const loadCurrentUser = async (): Promise<User | null> => {
  return await preferenceStorage.get('currentUser')
}

const loadSubscription = async (): Promise<Subscription | null> => {
  return await preferenceStorage.get('subscription')
}

const loadCalendarAccountToken = async (
  calendarAccountId: string,
  invalidGrantHandler: () => void,
  forceRefresh: boolean = false
): Promise<AccountTokenData> => {
  try {
    const params: {
      timestamp?: number
    } = {}

    if (forceRefresh) {
      params.timestamp = ZonedDateTime.now().toEpochSecond()
    }

    const tokenResponse = await useApi()
      .me()
      .getCalendarAccountToken(
        {
          calendarAccountId,
          forceRefresh: forceRefresh ? 'true' : 'false'
        },
        { params }
      )

    return {
      ...tokenResponse.data,
      expires_at: ZonedDateTime.parse(tokenResponse.data.expires_at)
    }
  } catch (e: any) {
    // Handle account-specific errors
    if (e.response?.status === 404) {
      throw new Error(`Calendar account ${calendarAccountId} not found or disconnected`)
    }
    // Optional-chain the whole path: an error response whose body has no
    // `errors` array (a 500 with an HTML body, a 429 "Rate exceeded.") otherwise
    // makes `.some` throw here, masking the real error and skipping
    // invalidGrantHandler() (DESKTOP: ~156 events/wk on 4.6.0).
    if (e.response?.data?.errors?.some((error: any) => error.code === 'refresh_token_invalid')) {
      invalidGrantHandler()
    }
    throw e
  }
}

const saveAuthToken = (token: string | null): void => {
  if (token) {
    preferenceStorage.set('authToken', token)
  } else {
    preferenceStorage.delete('authToken')
  }
}

const saveUser = (user: User | null): void => {
  if (user) {
    preferenceStorage.set('currentUser', JSON.parse(JSON.stringify(user)))
  } else {
    preferenceStorage.delete('currentUser')
  }
}

const saveSubscription = (subscription: Subscription | null): void => {
  if (subscription) {
    preferenceStorage.set('subscription', JSON.parse(JSON.stringify(subscription)))
  } else {
    preferenceStorage.delete('subscription')
  }
}
